import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

// Skeleton for the main app layout during initial loading
export const AppSkeleton: React.FC = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header skeleton */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-blur]:bg-background/60">
        <div className="container flex h-14 items-center">
          {/* Logo skeleton */}
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-muted rounded animate-pulse" />
            <div className="h-6 w-24 bg-muted rounded animate-pulse" />
          </div>
          
          {/* Navigation skeleton */}
          <nav className="flex items-center space-x-6 ml-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-4 w-16 bg-muted rounded animate-pulse" />
            ))}
          </nav>
          
          {/* Right side actions skeleton */}
          <div className="ml-auto flex items-center space-x-4">
            <div className="w-8 h-8 bg-muted rounded animate-pulse" />
            <div className="w-8 h-8 bg-muted rounded animate-pulse" />
          </div>
        </div>
      </header>

      {/* Main content skeleton */}
      <div className="flex">
        {/* Sidebar skeleton */}
        <aside className="w-64 border-r bg-background/95 backdrop-blur supports-[backdrop-blur]:bg-background/60">
          <div className="p-4">
            <div className="h-4 w-32 bg-muted rounded animate-pulse mb-4" />
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-10 bg-muted rounded animate-pulse" />
              ))}
            </div>
          </div>
        </aside>

        {/* Main content area skeleton */}
        <main className="flex-1">
          <div className="container max-w-screen-2xl mx-auto pb-8">
            {/* Page title skeleton */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-5 h-5 bg-muted rounded animate-pulse" />
                <div className="h-8 w-32 bg-muted rounded animate-pulse" />
              </div>
            </div>

            {/* Dashboard stats skeleton */}
            <div className="grid gap-6 md:grid-cols-3 mb-8">
              {Array.from({ length: 3 }).map((_, i) => (
                <Card key={i} className="h-full">
                  <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                    <div className="h-4 w-24 bg-muted rounded animate-pulse" />
                    <div className="w-6 h-6 bg-muted rounded animate-pulse" />
                  </CardHeader>
                  <CardContent>
                    <div className="h-8 w-16 bg-muted rounded animate-pulse" />
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Room occupancy and network stats skeleton */}
            <div className="md:col-span-3 grid md:grid-cols-2 gap-6">
              {/* Room occupancy skeleton */}
              <Card className="h-full">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-muted rounded animate-pulse" />
                    <div className="h-5 w-32 bg-muted rounded animate-pulse" />
                  </div>
                  <div className="h-4 w-48 bg-muted rounded animate-pulse" />
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center justify-between border rounded p-3">
                        <div className="h-4 w-24 bg-muted rounded animate-pulse" />
                        <div className="h-6 w-8 bg-muted rounded animate-pulse" />
                      </div>
                      <div className="flex items-center justify-between border rounded p-3">
                        <div className="h-4 w-20 bg-muted rounded animate-pulse" />
                        <div className="h-6 w-8 bg-muted rounded animate-pulse" />
                      </div>
                    </div>
                    <div className="h-[200px] w-full bg-muted/20 rounded-md animate-pulse">
                      <div className="flex items-center justify-center h-full">
                        <div className="flex flex-col items-center space-y-4">
                          <div className="w-32 h-32 bg-muted rounded-full animate-pulse" />
                          <div className="h-4 w-32 bg-muted rounded animate-pulse" />
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Network stats skeleton */}
              <Card className="h-full">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-muted rounded animate-pulse" />
                    <div className="h-5 w-32 bg-muted rounded animate-pulse" />
                  </div>
                  <div className="h-4 w-48 bg-muted rounded animate-pulse" />
                </CardHeader>
                <CardContent>
                  <div className="h-[300px] w-full bg-muted/20 rounded-md animate-pulse">
                    <div className="flex items-center justify-center h-full">
                      <div className="flex flex-col items-center space-y-4">
                        <div className="w-24 h-24 bg-muted rounded animate-pulse" />
                        <div className="h-4 w-32 bg-muted rounded animate-pulse" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent activity and quick search skeleton */}
            <div className="grid gap-6 md:grid-cols-2 mt-6">
              <Card>
                <CardHeader>
                  <div className="h-5 w-32 bg-muted rounded animate-pulse" />
                  <div className="h-4 w-48 bg-muted rounded animate-pulse" />
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="border-l-4 border-primary pl-4 py-2">
                        <div className="h-4 w-32 bg-muted rounded animate-pulse mb-2" />
                        <div className="h-3 w-48 bg-muted rounded animate-pulse mb-1" />
                        <div className="h-3 w-24 bg-muted rounded animate-pulse" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="h-5 w-24 bg-muted rounded animate-pulse" />
                </CardHeader>
                <CardContent>
                  <div className="h-10 w-full bg-muted rounded animate-pulse" />
                </CardContent>
              </Card>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

// Loading screen shown while app chunks load (e.g. after login)
export const MinimalAppSkeleton: React.FC = () => {
  return (
    <div
      className="min-h-screen bg-background flex flex-col items-center justify-center gap-8 px-4"
      role="status"
      aria-label="Loading application"
    >
      {/* Logo */}
      <div className="flex items-center gap-3">
        <img
          src="/assets/nodus_logo-128.png"
          alt=""
          className="h-12 w-12 object-contain opacity-90"
          width={48}
          height={48}
        />
        <span className="text-xl font-semibold text-foreground tracking-tight">
          Nodus
        </span>
      </div>

      {/* Spinner + message */}
      <div className="flex flex-col items-center gap-4">
        <div
          className="h-10 w-10 rounded-full border-2 border-primary/30 border-t-primary animate-spin"
          aria-hidden
        />
        <p className="text-sm text-muted-foreground animate-pulse">
          Preparing your workspace…
        </p>
      </div>

      {/* Subtle progress bar */}
      <div className="w-56 h-0.5 bg-muted rounded-full overflow-hidden">
        <div className="h-full w-20 max-w-[40%] bg-primary/70 rounded-full animate-pulse" />
      </div>
    </div>
  );
}; 